import React from "react";
import { Link } from "react-router-dom";
import { Users, ChevronRight, Award, Clock, CheckCircle2 } from "lucide-react";
import { Avatar, Stars, Tag } from "@/components/brand";
import { CatIcon } from "@/components/icons";
import { formatDayTime, formatR } from "@/lib/format";
import { cn } from "@/lib/utils";

export function ClassCard({ item, compact }) {
  return (
    <Link to={`/class/${item.id}`} data-testid={`class-card-${item.id}`}
      className={cn("group block rounded-xl2 overflow-hidden bg-white border border-divider hover:shadow-lg transition-shadow", compact && "w-[260px] shrink-0")}>
      <div className="relative h-40">
        <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />
        <span className="absolute top-3 right-3 h-7 px-3 inline-flex items-center rounded-pill bg-brand text-white font-display font-semibold text-sm">{formatR(item.price)}</span>
        <div className="absolute left-3 right-3 bottom-3">
          <div className="font-display text-lg text-white truncate">{item.title}</div>
          <div className="font-body font-semibold text-xs text-white/90 mt-0.5">{formatDayTime(item.start_time)} · {item.duration_min} min</div>
        </div>
      </div>
      <div className="flex items-center gap-2 p-3">
        <Avatar uri={item.trainer_avatar} size={28} name={item.trainer_name} />
        <span className="font-body font-semibold text-[13px] text-ink truncate max-w-[120px]">{item.trainer_name}</span>
        <div className="flex-1" />
        <Users size={13} className="text-mutedgrey" />
        <span className="font-body text-xs text-mutedgrey">{item.spots_left} left</span>
      </div>
    </Link>
  );
}

export function TrainerCard({ item }) {
  const from = item.private_pricing?.length ? Math.min(...item.private_pricing.map((p) => p.price)) : null;
  return (
    <Link to={`/trainers/${item.user_id}`} data-testid={`trainer-card-${item.user_id}`}
      className="flex items-center gap-4 p-4 rounded-xl2 bg-white border border-divider hover:border-brand transition-colors">
      <Avatar uri={item.avatar} size={64} name={item.name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-display text-[17px] text-ink truncate">{item.name}</span>
          {item.verified ? <CheckCircle2 size={14} className="text-brand shrink-0" /> : null}
        </div>
        <div className="font-body text-[13px] text-mutedgrey truncate">{item.headline}</div>
        <div className="mt-0.5"><Stars rating={item.rating} size={12} count={item.review_count} /></div>
        <div className="flex gap-1.5 mt-1">{(item.specialties || []).slice(0, 2).map((s) => <Tag key={s} label={s} />)}</div>
      </div>
      <div className="flex flex-col items-end">
        {from !== null ? (<><span className="font-body text-[11px] text-mutedgrey">from</span><span className="font-display text-base text-brand">{formatR(from)}</span></>) : null}
        <ChevronRight size={20} className="text-mutedgrey" />
      </div>
    </Link>
  );
}

export function TrainerPortraitCard({ item }) {
  return (
    <Link to={`/trainers/${item.user_id}`} data-testid={`trainer-portrait-${item.user_id}`}
      className="relative block w-[170px] h-[220px] shrink-0 rounded-xl2 overflow-hidden bg-surface2">
      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />
      <div className="absolute left-3 right-3 bottom-3">
        <div className="font-display text-[17px] text-white truncate">{item.name}</div>
        <div className="font-body text-xs text-white/85 truncate">{item.headline}</div>
        <div className="flex items-center gap-1 mt-1 font-body font-semibold text-[11px] text-white">
          <Award size={12} className="text-brand" />{Number(item.rating).toFixed(1)} · {item.sessions_completed} sessions
        </div>
      </div>
    </Link>
  );
}

export function CategoryTile({ name, image, icon, onClick, testID, selected }) {
  return (
    <button data-testid={testID} onClick={onClick} className="flex flex-col items-center gap-1.5 w-[92px] shrink-0">
      <div className={cn("relative w-[84px] h-[84px] rounded-md overflow-hidden bg-brand-soft", selected && "ring-2 ring-brand")}>
        <img src={image} alt={name} className="w-full h-full object-cover" />
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-brand flex items-center justify-center">
          <CatIcon name={icon} className="text-white" size={14} />
        </div>
      </div>
      <span className="font-body font-semibold text-xs text-ink truncate max-w-full">{name}</span>
    </button>
  );
}

export function ChallengeCard({ item, compact }) {
  const pct = Math.min(100, Math.round((item.progress / item.goal) * 100));
  const tone = item.state === "active" ? "pink" : item.state === "upcoming" ? "grey" : "dark";
  return (
    <Link to={`/challenges/${item.id}`} data-testid={`challenge-card-${item.id}`}
      className={cn("block rounded-xl2 border border-divider bg-white p-4 hover:border-brand transition-colors", compact ? "w-[260px] shrink-0" : "")}>
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-full bg-brand-soft flex items-center justify-center"><CatIcon name={item.badge_icon} className="text-brand" size={18} /></div>
        <Tag label={item.state} tone={tone} />
      </div>
      <div className="font-display text-lg text-ink mt-2 truncate">{item.title}</div>
      <div className="font-body text-xs text-mutedgrey line-clamp-2 min-h-[32px]">{item.description}</div>
      {item.joined ? (
        <div className="mt-2">
          <div className="h-2 rounded-pill bg-surface2 overflow-hidden"><div className="h-full bg-brand rounded-pill" style={{ width: `${pct}%` }} /></div>
          <div className="font-body text-[11px] text-mutedgrey mt-1">{item.progress}/{item.goal} {item.metric} {item.completed ? "· completed" : ""}</div>
        </div>
      ) : (
        <div className="flex items-center gap-3 mt-2 font-body text-[11px] text-mutedgrey">
          <span className="inline-flex items-center gap-1"><Users size={12} />{item.participant_count}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} />{item.days_left}d left</span>
          <span className="inline-flex items-center gap-1"><Award size={12} className="text-brand" />{item.reward_points} pts</span>
        </div>
      )}
    </Link>
  );
}
